import React, { memo, useCallback } from 'react'
import { useSelector } from '@tabeeb/store'

import { Menu, MenuItem } from '@mui/material'

import { useDialogState } from '@tabeeb/modules/shared/utils/hooks'
import { getIsInviteSettingsEnabled } from '@tabeeb/modules/appConfigState/selectors'

import { EditContentUserQualificationsDialog } from '@tabeeb/modules/invites/components/EditContentUserQualificationsDialog'
import {
  InviteWithQualificationsResponse,
  UpdateContentUserQualificationsRequest,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { useQueryClient } from '@tanstack/react-query'
import { InvitesQueryKeys } from '@tabeeb/modules/invites/requests'
import { getShowManageQualificationsMenuItem } from '@tabeeb/modules/invites/selectors'
import EditInviteTimeDialog from '../EditInviteTimeDialog'
import EditInvitePasswordDialog from '../EditInvitePasswordDialog'

interface InviteMenuProps {
  invite: InviteWithQualificationsResponse
  anchorEl: Element | null
  open: boolean
  onClose: () => void
  onCancelInvite: (inviteId: number) => void
  onCopyInviteLink: (inviteId: number) => void
  onResendInvite: (inviteId: number) => void
  onRevokeQuickAccessLink: (inviteId: number) => void
  onSetQuickAccessLinkPassword: ({ Id, Password }: { Id: number; Password: string }) => void
  onUpdateInviteTime: ({ Id, StartDate, EndDate }: { Id: number; StartDate: string; EndDate: string }) => void
}

const InviteMenu: React.FC<InviteMenuProps> = ({
  anchorEl,
  open,
  onClose,
  invite,
  onCancelInvite,
  onCopyInviteLink,
  onResendInvite,
  onRevokeQuickAccessLink,
  onSetQuickAccessLinkPassword,
  onUpdateInviteTime,
}) => {
  const isInviteSettingsEnabled = useSelector(getIsInviteSettingsEnabled)
  const [editTimeDialogOpen, onEditTimeDialogOpen, onEditTimeDialogClose] = useDialogState()
  const [editPasswordDialogOpen, onEditPasswordDialogOpen, onEditPasswordDialogClose] = useDialogState()
  const [editQualificationsDialogOpen, onEditQualificationsDialogOpen, onEditQualificationsDialogClose] =
    useDialogState()
  const queryClient = useQueryClient()
  const showManageQualificationsMenuItem = useSelector((state) =>
    getShowManageQualificationsMenuItem(state, { invite })
  )

  const onQualificationsUpdated = useCallback(
    (newValue: UpdateContentUserQualificationsRequest) => {
      queryClient.setQueryData<InviteWithQualificationsResponse[]>(
        [InvitesQueryKeys.ContentInvites, invite.ContentId],
        (prevInvites) => {
          if (!prevInvites) {
            return prevInvites
          }

          return prevInvites.map((i) => {
            if (i.ContentId === invite.ContentId && i.RecipientIdentityGuid === invite.RecipientIdentityGuid) {
              const newInvite = { ...i }
              newInvite.RequiredQualificationIds = [...newValue.QualificationIds]
              return newInvite
            }

            return i
          })
        }
      )
    },
    [invite.ContentId, invite.RecipientIdentityGuid, queryClient]
  )

  return (
    <>
      <Menu
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={onClose}
        onClick={onClose}
      >
        {!invite?.Accepted && <MenuItem onClick={() => onCancelInvite(invite.Id)}>Cancel</MenuItem>}
        {isInviteSettingsEnabled && <MenuItem onClick={onEditTimeDialogOpen}>Edit time</MenuItem>}
        {isInviteSettingsEnabled && !invite?.IdentityProtected && (
          <MenuItem onClick={onEditPasswordDialogOpen}>Add password</MenuItem>
        )}
        {isInviteSettingsEnabled && invite?.IdentityProtected && (
          <MenuItem onClick={() => onRevokeQuickAccessLink(invite.Id)}>Revoke quick link</MenuItem>
        )}
        {isInviteSettingsEnabled && invite?.IdentityProtected && (
          <MenuItem onClick={onEditPasswordDialogOpen}>Update password</MenuItem>
        )}
        {!invite?.Accepted && !invite?.Ignored && !invite?.IdentityProtected && (
          <MenuItem onClick={() => onResendInvite(invite.Id)}>Resend</MenuItem>
        )}
        <MenuItem onClick={() => onCopyInviteLink(invite.Id)}>Copy link</MenuItem>
        {showManageQualificationsMenuItem && (
          <MenuItem onClick={onEditQualificationsDialogOpen}>Manage required qualifications</MenuItem>
        )}
      </Menu>

      <EditInviteTimeDialog
        invite={invite}
        open={editTimeDialogOpen}
        onClose={onEditTimeDialogClose}
        onSave={onUpdateInviteTime}
      />
      <EditInvitePasswordDialog
        invite={invite}
        open={editPasswordDialogOpen}
        onClose={onEditPasswordDialogClose}
        onSave={onSetQuickAccessLinkPassword}
      />
      {invite && (
        <EditContentUserQualificationsDialog
          userId={invite.RecipientId!}
          userIdentityGuid={invite.RecipientIdentityGuid}
          userName={invite.Recipient?.Name || invite.RecipientEmail!}
          contentId={invite.ContentId}
          qualificationIds={invite.RequiredQualificationIds || []}
          open={editQualificationsDialogOpen}
          onClose={onEditQualificationsDialogClose}
          onUpdateSuccess={onQualificationsUpdated}
        />
      )}
    </>
  )
}

export default memo(InviteMenu)
